import React, { useCallback, useEffect, useRef, useState } from "react";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";
import { isMindmapElementHidden, findTopMostElement } from "@excalidraw/element";
import type { NonDeletedElementsMap } from "@excalidraw/element/types";
import { toBrandedType } from "@excalidraw/common";
import type { ElementComment } from "../data/api";
import {
  apiListComments,
  apiCreateComment,
  apiUpdateComment,
  apiDeleteComment,
} from "../data/api";
import { DrawingStore } from "../data/DrawingStore";
import { useDebouncedSave } from "../hooks/useDebouncedSave";

import "./ElementComments.scss";

interface ElementCommentsProps {
  excalidrawAPI: ExcalidrawImperativeAPI;
}

// Persist panel dimensions per element in localStorage
const SIZES_KEY = "excalidraw-comment-sizes";

function loadSizes(): Record<string, { w: number; h: number }> {
  try {
    return JSON.parse(localStorage.getItem(SIZES_KEY) || "{}");
  } catch {
    return {};
  }
}

function saveSize(elementId: string, w: number, h: number) {
  const sizes = loadSizes();
  sizes[elementId] = { w, h };
  localStorage.setItem(SIZES_KEY, JSON.stringify(sizes));
}

/** Set of element types that can have comments (shapes geométricos). */
const COMMENTABLE_TYPES = new Set(["rectangle", "diamond", "ellipse"]);

/** Simple bounding-box hit test in scene coordinates. */
function getElementAtPoint(
  elements: readonly any[],
  sceneX: number,
  sceneY: number,
) {
  return findTopMostElement(
    elements,
    sceneX,
    sceneY,
    (el) => COMMENTABLE_TYPES.has((el as any).type),
  );
}

function sceneToScreen(
  sceneX: number,
  sceneY: number,
  appState: {
    scrollX: number;
    scrollY: number;
    zoom: { value: number };
    offsetLeft: number;
    offsetTop: number;
  },
) {
  return {
    x: (sceneX + appState.scrollX) * appState.zoom.value + appState.offsetLeft,
    y: (sceneY + appState.scrollY) * appState.zoom.value + appState.offsetTop,
  };
}

function clientToScene(
  clientX: number,
  clientY: number,
  appState: {
    scrollX: number;
    scrollY: number;
    zoom: { value: number };
    offsetLeft: number;
    offsetTop: number;
  },
) {
  return {
    x:
      (clientX - appState.offsetLeft) / appState.zoom.value - appState.scrollX,
    y:
      (clientY - appState.offsetTop) / appState.zoom.value - appState.scrollY,
  };
}

/** Build set of element IDs hidden by mindmap collapse. */
function getHiddenElementIds(
  elements: readonly { id: string; boundElements?: readonly { id: string }[] | null }[],
) {
  const fullMap = toBrandedType<NonDeletedElementsMap>(new Map());
  for (const el of elements) {
    fullMap.set(el.id, el as any);
  }
  const hidden = new Set<string>();
  for (const el of elements) {
    if (isMindmapElementHidden(el as any, fullMap)) {
      hidden.add(el.id);
      if ((el as any).boundElements) {
        for (const b of (el as any).boundElements) {
          hidden.add(b.id);
        }
      }
    }
  }
  return hidden;
}

/** Get bottom-center position of an element in screen coords. */
function getElementBottomCenter(
  el: { x: number; y: number; width: number; height: number },
  appState: {
    scrollX: number;
    scrollY: number;
    zoom: { value: number };
    offsetLeft: number;
    offsetTop: number;
  },
) {
  const cx = el.x + el.width / 2;
  const bottom = Math.max(el.y, el.y + el.height);
  return sceneToScreen(cx, bottom, appState);
}

/** Get the bounding-box bottom-center of multiple elements in screen coords. */
function getGroupBottomCenter(
  groupElements: readonly {
    x: number;
    y: number;
    width: number;
    height: number;
  }[],
  appState: {
    scrollX: number;
    scrollY: number;
    zoom: { value: number };
    offsetLeft: number;
    offsetTop: number;
  },
) {
  let minX = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const el of groupElements) {
    minX = Math.min(minX, Math.min(el.x, el.x + el.width));
    maxX = Math.max(maxX, Math.max(el.x, el.x + el.width));
    maxY = Math.max(maxY, Math.max(el.y, el.y + el.height));
  }
  const cx = (minX + maxX) / 2;
  return sceneToScreen(cx, maxY, appState);
}

/**
 * Resolve the comment target for an element.
 * Grouped elements → `group:<outermostGroupId>`.
 * Ungrouped → element id (only for commentable types).
 * Returns null if the element is not commentable.
 */
function resolveCommentTarget(
  element: any,
  allElements: readonly any[],
): string | null {
  const groupIds = element.groupIds as readonly string[] | undefined;
  if (groupIds && groupIds.length > 0) {
    // Só permite comentário no grupo se ele contém pelo menos um shape comentável
    const outermostGroupId = groupIds[groupIds.length - 1];
    const groupEls = allElements.filter(
      (el) => !el.isDeleted && el.groupIds?.includes(outermostGroupId),
    );
    const hasCommentableShape = groupEls.some((el) =>
      COMMENTABLE_TYPES.has(el.type),
    );
    if (!hasCommentableShape) {
      return null;
    }
    // Elemento não-comentável dentro do grupo (ex: linha/seta) → retorna o grupo
    // mas só se o hover for num shape comentável
    if (!COMMENTABLE_TYPES.has(element.type)) {
      return null;
    }
    return `group:${outermostGroupId}`;
  }
  if (COMMENTABLE_TYPES.has(element.type)) {
    return element.id;
  }
  return null;
}

/** Get all non-deleted elements that belong to a given group. */
function getGroupElements(groupId: string, allElements: readonly any[]) {
  return allElements.filter(
    (el) => !el.isDeleted && el.groupIds?.includes(groupId),
  );
}

export const ElementComments = ({ excalidrawAPI }: ElementCommentsProps) => {
  const [hoveredElementId, setHoveredElementId] = useState<string | null>(null);
  const [activeElementId, setActiveElementId] = useState<string | null>(null);
  const [noteText, setNoteText] = useState("");
  const [activeCommentId, setActiveCommentId] = useState<string | null>(null);
  const [buttonPos, setButtonPos] = useState({ x: 0, y: 0 });
  const [panelPos, setPanelPos] = useState({ x: 0, y: 0 });
  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");

  const [commentsMap, setCommentsMap] = useState<
    Record<string, ElementComment>
  >({});

  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const anchorRef = useRef<string | null>(null);

  // Refs to avoid stale closures in saveNow
  const noteTextRef = useRef(noteText);
  noteTextRef.current = noteText;
  const activeCommentIdRef = useRef(activeCommentId);
  activeCommentIdRef.current = activeCommentId;

  // Tracks whether the mouse is currently over the hover button
  const isOverButtonRef = useRef(false);

  const drawingId = DrawingStore.getActiveDrawingId();

  // ── Load comments on mount ──
  useEffect(() => {
    if (!drawingId) {
      return;
    }
    apiListComments(drawingId)
      .then((list) => {
        const map: Record<string, ElementComment> = {};
        for (const c of list) {
          if (
            !map[c.element_id] ||
            new Date(c.created_at) > new Date(map[c.element_id].created_at)
          ) {
            map[c.element_id] = c;
          }
        }
        setCommentsMap(map);
      })
      .catch(() => {});
  }, [drawingId]);

  // ── Position helpers (bottom-center of element) ──
  const updatePositions = useCallback(
    (targetId: string) => {
      const appState = excalidrawAPI.getAppState();
      const elements = excalidrawAPI.getSceneElements();
      let pos: { x: number; y: number };

      if (targetId.startsWith("group:")) {
        // Posiciona no shape âncora (o que foi hovered), não no centro do grupo
        const anchor = anchorRef.current
          ? elements.find((e) => e.id === anchorRef.current)
          : null;
        if (anchor) {
          pos = getElementBottomCenter(anchor, appState);
        } else {
          // Fallback: primeiro shape comentável do grupo
          const commentableEls = getGroupElements(
            targetId.slice(6),
            elements,
          ).filter((el: any) => COMMENTABLE_TYPES.has(el.type));
          if (commentableEls.length === 0) {
            return;
          }
          pos = getElementBottomCenter(commentableEls[0], appState);
        }
      } else {
        const el = elements.find((e) => e.id === targetId);
        if (!el) {
          return;
        }
        pos = getElementBottomCenter(el, appState);
      }

      setButtonPos({ x: pos.x, y: pos.y + 6 });
      setPanelPos({ x: pos.x, y: pos.y + 10 });
    },
    [excalidrawAPI],
  );

  // ── Reposition every frame (tracks drag, scroll, zoom in real time) ──
  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    const targetId = activeElementId || hoveredElementId;
    if (!targetId) {
      return;
    }
    let rafId: number;
    const tick = () => {
      const elements = excalidrawAPI.getSceneElements();
      const hiddenIds = getHiddenElementIds(elements);

      if (targetId.startsWith("group:")) {
        const groupEls = getGroupElements(targetId.slice(6), elements);
        const allHidden =
          groupEls.length === 0 ||
          groupEls.every((el: any) => hiddenIds.has(el.id));
        if (allHidden) {
          if (activeElementId) {
            flushAndClose();
          }
          setHoveredElementId(null);
          return;
        }
      } else if (hiddenIds.has(targetId)) {
        if (activeElementId) {
          flushAndClose();
        }
        setHoveredElementId(null);
        return;
      }

      updatePositions(targetId);
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [excalidrawAPI, hoveredElementId, activeElementId, updatePositions]);

  // ── Hover detection ──
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (activeElementId) {
        return;
      }
      // If cursor is over the hover button, keep it visible
      if (isOverButtonRef.current) {
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }
        return;
      }
      const appState = excalidrawAPI.getAppState();
      const scene = clientToScene(e.clientX, e.clientY, appState);
      const elements = excalidrawAPI.getSceneElements();
      const hiddenIds = getHiddenElementIds(elements);
      const hit = getElementAtPoint(elements, scene.x, scene.y);

      if (hit && !hiddenIds.has(hit.id)) {
        const targetId = resolveCommentTarget(hit, elements);
        if (!targetId) {
          // Element is not commentable — start hide timer
          if (hoveredElementId && !hideTimeoutRef.current) {
            hideTimeoutRef.current = setTimeout(() => {
              setHoveredElementId(null);
              hideTimeoutRef.current = null;
            }, 400);
          }
          return;
        }
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }
        if (targetId !== hoveredElementId) {
          setHoveredElementId(targetId);
          anchorRef.current = hit.id;
          updatePositions(targetId);
        }
      } else if (hoveredElementId && !hideTimeoutRef.current) {
        hideTimeoutRef.current = setTimeout(() => {
          setHoveredElementId(null);
          hideTimeoutRef.current = null;
        }, 400);
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [excalidrawAPI, hoveredElementId, activeElementId, updatePositions]);

  // ── Click outside to close ──
  useEffect(() => {
    if (!activeElementId) {
      return;
    }
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        panelRef.current?.contains(target) ||
        buttonRef.current?.contains(target)
      ) {
        return;
      }
      flushAndClose();
    };
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleMouseDown);
    }, 50);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [activeElementId]);

  // ── Save logic + auto-save (debounced) via shared hook ──
  const { flush: flushSave } = useDebouncedSave({
    value: noteText,
    delayMs: 800,
    enabled: !!activeElementId && !!drawingId,
    onStatus: setSaveStatus,
    save: async (text) => {
      if (!activeElementId || !drawingId) {
        return;
      }
      const trimmed = text.trim();
      const commentId = activeCommentIdRef.current;

      if (trimmed && commentId) {
        const updated = await apiUpdateComment(drawingId, commentId, trimmed);
        setCommentsMap((prev) => ({
          ...prev,
          [updated.element_id]: updated,
        }));
      } else if (trimmed && !commentId) {
        const created = await apiCreateComment(
          drawingId,
          activeElementId,
          trimmed,
        );
        setActiveCommentId(created.id);
        setCommentsMap((prev) => ({
          ...prev,
          [created.element_id]: created,
        }));
      } else if (!trimmed && commentId) {
        await apiDeleteComment(drawingId, commentId);
        setCommentsMap((prev) => {
          const next = { ...prev };
          delete next[activeElementId];
          return next;
        });
        setActiveCommentId(null);
      }
    },
  });

  // ── Persist size on resize ──
  useEffect(() => {
    if (!activeElementId || !panelRef.current) {
      return;
    }
    const el = panelRef.current.querySelector(
      ".comment-textarea",
    ) as HTMLElement | null;
    if (!el) {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0 && activeElementId) {
          saveSize(activeElementId, Math.round(width), Math.round(height));
        }
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [activeElementId]);

  const handleOverlayMouseEnter = () => {
    isOverButtonRef.current = true;
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleOverlayMouseLeave = () => {
    isOverButtonRef.current = false;
    if (!activeElementId) {
      hideTimeoutRef.current = setTimeout(() => {
        setHoveredElementId(null);
        hideTimeoutRef.current = null;
      }, 400);
    }
  };

  // ── Open panel ──
  const openPanel = (elementId: string) => {
    setActiveElementId(elementId);
    const existing = commentsMap[elementId];
    setNoteText(existing?.text || "");
    setActiveCommentId(existing?.id || null);
    setSaveStatus("idle");
    updatePositions(elementId);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  // ── Close panel (flush save first) ──
  const flushAndClose = () => {
    flushSave();
    setActiveElementId(null);
    setNoteText("");
    setActiveCommentId(null);
    setSaveStatus("idle");
  };

  // ── Get saved size for an element ──
  const getSavedSize = (elementId: string) => {
    const sizes = loadSizes();
    return sizes[elementId] || null;
  };

  // ── Render indicators ──
  const renderIndicators = () => {
    const appState = excalidrawAPI.getAppState();
    const elements = excalidrawAPI.getSceneElements();
    const hiddenIds = getHiddenElementIds(elements);
    const indicators: React.ReactElement[] = [];

    for (const targetId of Object.keys(commentsMap)) {
      if (targetId === hoveredElementId || targetId === activeElementId) {
        continue;
      }

      let pos: { x: number; y: number };

      if (targetId.startsWith("group:")) {
        const commentableEls = getGroupElements(targetId.slice(6), elements)
          .filter((el: any) => COMMENTABLE_TYPES.has(el.type));
        if (
          commentableEls.length === 0 ||
          commentableEls.every((el: any) => hiddenIds.has(el.id))
        ) {
          continue;
        }
        pos = getElementBottomCenter(commentableEls[0], appState);
      } else {
        const el = elements.find((e) => e.id === targetId);
        if (!el || hiddenIds.has(el.id)) {
          continue;
        }
        pos = getElementBottomCenter(el, appState);
      }

      indicators.push(
        <div
          key={targetId}
          className="comment-indicator"
          style={{ left: pos.x, top: pos.y + 4 }}
          onClick={() => openPanel(targetId)}
        >
          <CommentIcon size={14} />
        </div>,
      );
    }

    return indicators;
  };

  const showButton = hoveredElementId && !activeElementId;
  const savedSize = activeElementId ? getSavedSize(activeElementId) : null;

  return (
    <div className="element-comments-overlay">
      {renderIndicators()}

      {showButton && (
        <button
          ref={buttonRef}
          className="comment-hover-button"
          style={{ left: buttonPos.x, top: buttonPos.y }}
          onMouseEnter={handleOverlayMouseEnter}
          onMouseLeave={handleOverlayMouseLeave}
          onClick={() => openPanel(hoveredElementId!)}
          title="Comentário"
        >
          <CommentIcon size={16} />
        </button>
      )}

      {activeElementId && (
        <div
          ref={panelRef}
          className="comment-panel"
          style={{ left: panelPos.x, top: panelPos.y }}
        >
          <textarea
            ref={textareaRef}
            className="comment-textarea"
            placeholder="Escreva um comentário..."
            value={noteText}
            style={{
              ...(savedSize
                ? { width: savedSize.w, height: savedSize.h }
                : {}),
            }}
            onChange={(e) => setNoteText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                flushAndClose();
              }
              e.stopPropagation();
            }}
          />
          {saveStatus !== "idle" && (
            <div className={`comment-save-status comment-save-status--${saveStatus}`}>
              {saveStatus === "saving" && "Salvando..."}
              {saveStatus === "saved" && "Salvo ✓"}
              {saveStatus === "error" && "Erro ao salvar"}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CommentIcon = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
